module.exports = /* @ngInject */
() => {
    /**
     * Convert JavaScript Date object to UTC (GMT) timezone Date object
     * @param  {Object} date JavaScript Date object
     * @return {Object}      UTC converted JavaScript Date object
     */
    function convertToUTC(date) {
        return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    }

    // conversion constants
    const hoursPerDay = 24;
    const minsPerHour = 60;
    const msPerSec = 1000;
    const msPerMin = 60000;
    const secsPerMin = 60;

    return {
        /**
         * Convert YYYY-MM-DD time string to MM/DD/YYYY
         * @param  {String} date YYYY-MM-DD formatted string to convert
         * @return {String}      MM/DD/YYYY formatted string
         */
        forceSlashFormat(date) {
            let yy;
            let mm;
            let dd;

            // convert YYYY-MM-DD to MM/DD/YYYY
            if (date.substring(4, 5) === '-' && date.substring(7, 8) === '-') {
                yy = date.substring(0, 4);
                mm = date.substring(5, 7);
                dd = date.substring(8, 10);
            } else {
                return date;
            }

            return mm + '/' + dd + '/' + yy;
        },

        /**
         * Convert from minutes to milliseconds
         * @param  {Number} mins Number of minutes to convert
         * @return {Number}      Milliseconds conversion
         */
        fromMinsToMS(mins) {
            return mins * msPerMin;
        },

        /**
         * Convert from minutes to seconds
         * @param  {Number} mins Number of minutes to convert
         * @return {Number}      Seconds conversion
         */
        fromMinsToSecs(mins) {
            return mins * secsPerMin;
        },

        /**
         * Convert from milliseconds to days
         * @param  {Number} ms Number of milliseconds to convert
         * @return {Number}    Days conversion
         */
        fromMSToDays(ms) {
            return ms / (msPerSec * secsPerMin * minsPerHour * hoursPerDay);
        },

        /**
         * Convert from milliseconds to minutes
         * @param  {Number} ms Number of milliseconds to convert
         * @return {Number}    Minutes conversion
         */
        fromMStoMins(ms) {
            return ms / msPerMin;
        },

        /**
         * Convert from milliseconds to seconds
         * @param  {Number} ms Number of milliseconds to convert
         * @return {Number}    Seconds conversion
         */
        fromMStoSecs(ms) {
            return ms / msPerSec;
        },

        /**
         * Convert from seconds to days
         * @param  {Number} secs Number of seconds to convert
         * @return {Number}    Days conversion
         */
        fromSecsToDays(secs) {
            return secs / (secsPerMin * minsPerHour * hoursPerDay);
        },

        /**
         * Convert from seconds to minutes
         * @param  {Number} secs Number of seconds to convert
         * @return {Number}      Minutes conversion
         */
        fromSecsToMins(secs) {
            return secs / secsPerMin;
        },

        /**
         * Convert from seconds to milliseconds
         * @param  {Number} secs Number of seconds to convert
         * @return {Number}      Milliseconds conversion
         */
        fromSecsToMS(secs) {
            return secs * msPerSec;
        },

        /**
         * Get month, day and year values from a passed-in
         * unix timestamp (secs).
         * @param  {Number} unixTimestamp Timestamp in secs
         * @return {Object}               Contains month, day and year
         */
        getDateValues(unixTimestamp) {
            const readableObj = this.getReadableTime(unixTimestamp);
            const month = this.padZero(readableObj.month);
            const day = this.padZero(readableObj.date);

            return {
                day: day,
                month: month,
                year: readableObj.year
            };
        },

        /**
         * Convert from months to milliseconds
         * @param  {Number} months Number of months to convert
         * @return {Number}        Milliseconds conversion
         */
        getMonthsFromNowInMS(months) {
            const date = new Date();
            date.setMonth(date.getMonth() - months);
            return date.getTime();
        },

        /**
         * Get the previous date in the past from
         * the passed-in ms of elapsed time
         * @param  {Number} ms Time elapsed to past date
         * @return {Object}    Past date
         */
        getPreviousDate(ms) {
            const now = new Date();
            return new Date(now.getTime() - ms);
        },

        /**
         * Convert Unix timestamp into an Object
         * with readable, time-based properties.
         * @param  {Number} unixTimestamp Unix Timestamp
         * @return {Object}               Readable, time-based Object
         */
        getReadableTime(unixTimestamp) {
            const timestamp = unixTimestamp ? this.fromSecsToMS(unixTimestamp) : this.getUTCFromMS(Date.now());
            const dateObj = new Date(timestamp);
            const date = dateObj.getDate();
            const month = dateObj.getMonth() + 1;
            const day = dateObj.getDay();
            const year = dateObj.getFullYear();
            const hour = dateObj.getHours() + 1;

            return {
                date: date,
                month: month,
                day: day,
                year: year,
                hour: hour
            };
        },

        /**
         * Convert a JavaScript Date millisecond timestamp
         * into a Unix timestamp (in secs).
         * @param  {Number} timestamp JavaScript Date millisecond timestamp
         * @return {Number}           Unix timestamp (in secs)
         */
        getUnixTimestamp(timestamp) {
            const unixTimestamp = new Date(timestamp).getTime();
            return parseInt(unixTimestamp / 1000, 10);
        },

        /**
         * Get UTC (GMT) time from a JavaScript Date Object
         * @param  {Object} date JavaScript Date
         * @return {Number}      UTC conversion
         */
        getUTC(date) {
            return convertToUTC(date);
        },

        /**
         * Convert JavaScript Date millisecond timestamp to UTC (GMT) time
         * @param  {Number} timestamp JavaScript Date millisecond timestamp
         * @return {Number}           UTC conversion
         */
        getUTCFromMS(timestamp) {
            const date = new Date(timestamp);
            return convertToUTC(date);
        },

        /**
         * Convert Unix timestamp to UTC (GMT) time
         * @param  {Number} unixTimestamp Unix timestamp
         * @return {Number}               UTC conversion
         */
        getUTCFromUnix(unixTimestamp) {
            const timestamp = this.fromSecsToMS(unixTimestamp);
            const date = new Date(timestamp);
            const gmtDate = convertToUTC(date);

            return this.getUnixTimestamp(gmtDate);
        },

        // takes date
        // returns gmt unix timestamp

        /**
         * Convert a JavaScript Date object into a Unix-based GMT timestamp
         * @param  {Object} date JavaScript Date object
         * @return {Number}      Unix-based GMT conversion
         */
        getUTCUnixFromDate(date) {
            const gmtDate = this.getUTC(date);
            return this.getUnixTimestamp(gmtDate.getTime());
        },

        /**
         * Convert a JavaScript Date millisecond timestamp
         * intoa Unix-based GMT timestamp
         * @param  {Number} timestamp JavaScript Date millisecond timestamp
         * @return {Number}      Unix-based GMT conversion
         */
        getUTCUnixFromMS(timestamp) {
            const gmtTimestamp = this.getUTCFromMS(timestamp);
            return this.getUnixTimestamp(gmtTimestamp);
        },

        /**
         * Pads a single digit string with a leading zero.
         * @param  {Number} num Number to be padded
         * @return {String}     Padded number
         */
        padZero(num) {
            return num < 10 ? '0' + num : num.toString();
        }
    };
};
